import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { CopyLinkButton } from '@/components/assessment-engine/CopyLinkButton';
import { PublicHeader } from '@/components/public/PublicHeader';
import { QuestionRenderer, QuestionAnswerValue } from '@/components/assessment-engine/QuestionRenderer';
import { DemographicQuestionsGrid } from '@/components/assessment-engine/DemographicQuestionsGrid';
import {
  contiguousDemographicRange,
  isDemographicsQuestionList,
} from '@/components/assessment-engine/demographicFieldLayout';
import { flattenVisibleQuestions } from '@/components/assessment-engine/flattenSections';
import { TakeAssessmentCampaignTitle } from '@/components/assessment-engine/TakeAssessmentCampaignTitle';
import { TakeAssessmentOpeningFraming } from '@/components/assessment-engine/TakeAssessmentOpeningFraming';
import { getNextQuestionKey, orderQuestionsByNavigation, nextPresentationStepIndex } from '@/components/assessment-engine/questionNavigation';
import {
  buildPresentationSteps,
  stepIndexForQuestionKey,
  type PresentationStep,
} from '@/components/assessment-engine/presentationSteps';
import {
  AE_ASSESSMENT_CONTENT_WIDTH,
  AE_BTN_PRIMARY,
  AE_BTN_SECONDARY,
  AE_CARD,
  AE_CARD_COMPACT,
  AE_INPUT,
  AE_INPUT_COMPACT,
  AE_LABEL,
  AE_MUTED,
  AE_PUBLIC_PAGE,
  AE_SECTION_TITLE,
  AE_SPINNER,
  AE_TITLE,
} from '@/components/assessment-engine/layout/aeTheme';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import {
  isRequiredQuestionAnswered,
  unansweredRequiredQuestions,
} from '@/components/assessment-engine/answerValidation';
import { answerValueToMapEntry } from '@/components/assessment-engine/answerMapEntry';
import { resolveNextQuestionIndex } from '@/components/assessment-engine/resolveNextQuestionIndex';
import {
  buildAnswersByQuestionId,
  type PublicAnswerRow,
} from '@/components/assessment-engine/hydrateQuestionAnswers';
import type {
  AeBranding,
  AeCompletionOptions,
  AeEvaluatedQuestion,
  AeEvaluatedSection,
  AePresentationStyle,
  AePublicCampaign,
  AeTemplateLogicRule,
} from '@/types/assessmentEngine';

type Step =
  | 'loading'
  | 'email'
  | 'otp'
  | 'welcome_back'
  | 'questions'
  | 'complete'
  | 'closed'
  | 'error';

function parseBranding(raw: AePublicCampaign['branding']): AeBranding {
  if (!raw || typeof raw !== 'object') return {};
  return raw as AeBranding;
}

/** Public respondent flow — invite email verification, then template-driven survey. */
export const TakeAssessmentPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { linkToken } = useParams<{ linkToken: string }>();
  const [searchParams] = useSearchParams();
  const inviteEmailFromUrl = searchParams.get('email')?.trim() ?? '';
  const inviteClickTrackedRef = useRef(false);
  const [step, setStep] = useState<Step>('loading');
  const [campaign, setCampaign] = useState<AePublicCampaign | null>(null);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [responseId, setResponseId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<AeEvaluatedQuestion[]>([]);
  const [sections, setSections] = useState<AeEvaluatedSection[]>([]);
  const [logicRules, setLogicRules] = useState<AeTemplateLogicRule[]>([]);
  const [answerMap, setAnswerMap] = useState<Record<string, string | number | boolean | null>>({});
  const [answersByQuestionId, setAnswersByQuestionId] = useState<Record<string, QuestionAnswerValue>>({});
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState<QuestionAnswerValue>({});
  const [presentationStyle, setPresentationStyle] = useState<AePresentationStyle>('question');
  const [presentationSteps, setPresentationSteps] = useState<PresentationStep[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepAnswers, setStepAnswers] = useState<Record<string, QuestionAnswerValue>>({});
  const [completionOptions, setCompletionOptions] = useState<Partial<AeCompletionOptions>>({});
  const [distributionMode, setDistributionMode] = useState<string>('public');
  const [inviteEmails, setInviteEmails] = useState('');
  const [cohortSubscribed, setCohortSubscribed] = useState(false);
  const [showInstantResults, setShowInstantResults] = useState(false);
  const [postBusy, setPostBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const branding = parseBranding(campaign?.branding);
  const current = questions[index];
  const currentPresentationStep = presentationSteps[stepIndex];
  const isPagedMode = presentationStyle !== 'question';

  /** One-at-a-time mode: show the whole About you block as a compact grid. */
  const questionModeDemoRange = useMemo(
    () => (!isPagedMode ? contiguousDemographicRange(questions, index) : null),
    [isPagedMode, questions, index]
  );
  const questionModeDemoQuestions = useMemo(() => {
    if (!questionModeDemoRange) return null;
    return questions.slice(questionModeDemoRange.start, questionModeDemoRange.end + 1);
  }, [questions, questionModeDemoRange]);

  useEffect(() => {
    if (!questionModeDemoRange || index === questionModeDemoRange.start) return;
    setIndex(questionModeDemoRange.start);
  }, [questionModeDemoRange, index]);

  const patchAnswerForQuestion = useCallback((questionId: string, value: QuestionAnswerValue) => {
    setAnswersByQuestionId((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const restoreStepAnswersFromCache = useCallback(
    (step: PresentationStep | undefined): Record<string, QuestionAnswerValue> => {
      if (!step) return {};
      const restored: Record<string, QuestionAnswerValue> = {};
      for (const q of step.questions) {
        restored[q.id] = answersByQuestionId[q.id] ?? {};
      }
      return restored;
    },
    [answersByQuestionId]
  );

  useEffect(() => {
    if (step !== 'questions' || isPagedMode || !current) return;
    setAnswer(answersByQuestionId[current.id] ?? {});
  }, [step, isPagedMode, index, current?.id, answersByQuestionId]);

  useEffect(() => {
    if (step !== 'questions' || !isPagedMode || !currentPresentationStep) return;
    setStepAnswers(restoreStepAnswersFromCache(currentPresentationStep));
  }, [step, isPagedMode, stepIndex, currentPresentationStep?.id, restoreStepAnswersFromCache]);

  useEffect(() => {
    if (!linkToken || !inviteEmailFromUrl || inviteClickTrackedRef.current) return;
    inviteClickTrackedRef.current = true;
    setEmail(inviteEmailFromUrl);
    void assessmentEngineService.recordInviteClick(linkToken, inviteEmailFromUrl).catch(() => {
      // Click tracking is best-effort; respondent flow continues even if it fails.
    });
  }, [linkToken, inviteEmailFromUrl]);

  useEffect(() => {
    if (!linkToken) {
      setStep('error');
      return;
    }
    assessmentEngineService
      .getPublicCampaign(linkToken)
      .then((c) => {
        setCampaign(c);
        if (c.status !== 'open') setStep('closed');
        else setStep('email');
      })
      .catch(() => setStep('error'));
  }, [linkToken]);

  const finishOrRedirect = async () => {
    if (!linkToken || !responseId) return;
    const completed = await assessmentEngineService.completeResponse(linkToken, responseId);
    if (completed.completion_options) {
      setCompletionOptions(completed.completion_options);
    }
    if (completed.distribution_mode) {
      setDistributionMode(completed.distribution_mode);
    }
    setShowInstantResults(!!completed.show_instant_results);
    setStep('complete');
  };

  const requestVerificationCode = async () => {
    if (!linkToken || !email.trim()) {
      toast.error(t('assessmentEngine.take.emailRequired'));
      return;
    }
    setSaving(true);
    try {
      await assessmentEngineService.requestRespondentOtp(linkToken, email.trim());
      toast.success(t('assessmentEngine.take.otpSent'));
      setVerificationCode('');
      setStep('otp');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      if (msg === 'NOT_INVITED') {
        toast.error(t('assessmentEngine.take.inviteOnly'));
      } else {
        toast.error(t('assessmentEngine.take.otpSendFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  /** Continue after email entry — OTP when required, otherwise start session immediately. */
  const continueFromEmail = async () => {
    if (campaign?.email_verification_required === false) {
      await startSession();
      return;
    }
    await requestVerificationCode();
  };

  const verifyAndStartSession = async () => {
    if (!linkToken || !email.trim() || verificationCode.trim().length < 6) return;
    setSaving(true);
    try {
      await assessmentEngineService.verifyRespondentOtp(
        linkToken,
        email.trim(),
        verificationCode.trim()
      );
      await startSession();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      if (msg === 'INVALID_OTP') {
        toast.error(t('assessmentEngine.take.otpInvalid'));
      } else {
        toast.error(t('assessmentEngine.take.otpVerifyFailed'));
      }
      setSaving(false);
    }
  };

  const startSession = async () => {
    if (!linkToken || !email.trim()) {
      toast.error(t('assessmentEngine.take.emailRequired'));
      return;
    }
    setSaving(true);
    try {
      const session = await assessmentEngineService.startSession(linkToken, {
        email: email.trim(),
      });
      const style = session.presentation_style ?? 'question';
      setPresentationStyle(style);
      setResponseId(session.response_id);
      setSections(session.sections);
      setLogicRules(session.logic_rules ?? []);

      // Completed for the current time period → results or thank-you (not a new take flow).
      if (session.status === 'complete') {
        if (session.completion_options) {
          setCompletionOptions(session.completion_options);
        }
        if (session.distribution_mode) {
          setDistributionMode(session.distribution_mode);
        }
        setShowInstantResults(!!session.show_instant_results);
        setStep('complete');
        return;
      }

      const initialAnswerMap = session.answer_map ?? {};
      setAnswerMap(initialAnswerMap);

      const flat = flattenVisibleQuestions(session.sections);
      const ordered = orderQuestionsByNavigation(
        flat,
        session.sections,
        session.logic_rules ?? [],
        initialAnswerMap
      );
      setQuestions(ordered);

      const questionsById = new Map(flat.map((q) => [q.id, q]));
      try {
        const rows = (await assessmentEngineService.getPublicAnswers(
          linkToken,
          session.response_id
        )) as PublicAnswerRow[];
        const hydratedAnswers = buildAnswersByQuestionId(rows, questionsById);
        setAnswersByQuestionId(hydratedAnswers);
      } catch {
        setAnswersByQuestionId({});
      }

      let resuming = false;
      if (style === 'question') {
        const resumeIdx = session.resume_question_key
          ? Math.max(0, ordered.findIndex((q) => q.question_key === session.resume_question_key))
          : 0;
        setIndex(resumeIdx >= 0 ? resumeIdx : 0);
        resuming = session.status === 'in_progress' && resumeIdx > 0;
      } else {
        const steps = buildPresentationSteps(style, session.sections);
        setPresentationSteps(steps);
        const sIdx = session.resume_question_key
          ? stepIndexForQuestionKey(steps, session.resume_question_key)
          : 0;
        setStepIndex(sIdx);
        resuming = session.status === 'in_progress' && sIdx > 0;
      }

      if (resuming) {
        setStep('welcome_back');
      } else {
        setStep('questions');
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('assessmentEngine.take.sessionFailed');
      if (msg === 'EMAIL_NOT_VERIFIED') {
        toast.error(t('assessmentEngine.take.emailNotVerified'));
        setStep('email');
      } else if (msg === 'NOT_INVITED') {
        toast.error(t('assessmentEngine.take.inviteOnly'));
        setStep('email');
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const saveAndAdvance = useCallback(async () => {
    if (!linkToken || !responseId || !current) return;

    const demoRange = contiguousDemographicRange(questions, index);
    const demoBlock =
      demoRange != null ? questions.slice(demoRange.start, demoRange.end + 1) : null;

    if (demoBlock && demoBlock.length > 0) {
      const answersForBlock: Record<string, QuestionAnswerValue> = {};
      for (const q of demoBlock) {
        answersForBlock[q.id] =
          q.id === current.id ? answer : (answersByQuestionId[q.id] ?? {});
      }
      const missing = unansweredRequiredQuestions(demoBlock, answersForBlock);
      if (missing.length > 0) {
        toast.error(t('assessmentEngine.take.requiredAnswer'));
        return;
      }
      setSaving(true);
      try {
        let latestSections = sections;
        const nextAnswers = { ...answerMap };
        for (const q of demoBlock) {
          const ans = answersForBlock[q.id] ?? {};
          const result = await assessmentEngineService.upsertAnswer(linkToken, responseId, {
            question_id: q.id,
            ...ans,
          });
          nextAnswers[q.question_key] = answerValueToMapEntry(ans);
          if (result.answer_map) Object.assign(nextAnswers, result.answer_map);
          if (result.sections) latestSections = result.sections;
        }
        setAnswerMap(nextAnswers);
        setSections(latestSections);
        setAnswersByQuestionId((prev) => ({ ...prev, ...answersForBlock }));

        const flat = flattenVisibleQuestions(latestSections);
        const ordered = orderQuestionsByNavigation(flat, latestSections, logicRules, nextAnswers);
        const lastKey = demoBlock[demoBlock.length - 1]!.question_key;
        const nextKey = getNextQuestionKey(lastKey, latestSections, logicRules, nextAnswers);
        if (!nextKey) {
          await finishOrRedirect();
        } else {
          const nextIdx = resolveNextQuestionIndex(ordered, flat, nextKey, lastKey, demoRange!.end);
          setQuestions(ordered);
          setIndex(nextIdx);
        }
      } catch {
        toast.error(t('assessmentEngine.take.saveFailed'));
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!isRequiredQuestionAnswered(current, answer)) {
      toast.error(t('assessmentEngine.take.requiredAnswer'));
      return;
    }
    setSaving(true);
    try {
      const answerKey = current.question_key;
      const nextAnswers: Record<string, string | number | boolean | null> = {
        ...answerMap,
        [answerKey]: answerValueToMapEntry(answer),
      };

      const result = await assessmentEngineService.upsertAnswer(linkToken, responseId, {
        question_id: current.id,
        ...answer,
      });

      const latestSections = result.sections ?? sections;
      const answersForNav = result.answer_map ?? nextAnswers;
      setAnswerMap(answersForNav);
      setSections(latestSections);
      setAnswersByQuestionId((prev) => ({ ...prev, [current.id]: answer }));

      const flat = flattenVisibleQuestions(latestSections);
      const ordered = orderQuestionsByNavigation(flat, latestSections, logicRules, answersForNav);
      // Forward nav only — never fall back to resume_question_key (that is first unanswered,
      // including skipped optionals, and would yank the respondent backward after the last question).
      const nextKey = getNextQuestionKey(answerKey, latestSections, logicRules, answersForNav);

      if (!nextKey) {
        await finishOrRedirect();
      } else {
        const nextIdx = resolveNextQuestionIndex(ordered, flat, nextKey, answerKey, index);
        setQuestions(ordered);
        setIndex(nextIdx);
      }
    } catch {
      toast.error(t('assessmentEngine.take.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [
    linkToken,
    responseId,
    current,
    answer,
    answerMap,
    answersByQuestionId,
    questions,
    sections,
    logicRules,
    index,
    t,
  ]);

  const saveStepAndAdvance = useCallback(async () => {
    if (!linkToken || !responseId || !currentPresentationStep) return;
    const missing = unansweredRequiredQuestions(
      currentPresentationStep.questions,
      stepAnswers
    );
    if (missing.length > 0) {
      toast.error(t('assessmentEngine.take.requiredAnswer'));
      return;
    }
    setSaving(true);
    try {
      let latestSections = sections;
      const nextAnswers = { ...answerMap };

      for (const q of currentPresentationStep.questions) {
        const ans = stepAnswers[q.id] ?? {};
        const result = await assessmentEngineService.upsertAnswer(linkToken, responseId, {
          question_id: q.id,
          ...ans,
        });
        nextAnswers[q.question_key] = answerValueToMapEntry(ans);
        if (result.answer_map) {
          Object.assign(nextAnswers, result.answer_map);
        }
        if (result.sections) latestSections = result.sections;
      }

      setAnswerMap(nextAnswers);
      setSections(latestSections);

      setAnswersByQuestionId((prev) => {
        const next = { ...prev };
        for (const q of currentPresentationStep.questions) {
          const ans = stepAnswers[q.id];
          if (ans) next[q.id] = ans;
        }
        return next;
      });

      const style = presentationStyle === 'subsection' ? 'subsection' : 'section';
      const rebuilt = buildPresentationSteps(style, latestSections);
      setPresentationSteps(rebuilt);

      const lastQ = currentPresentationStep.questions[currentPresentationStep.questions.length - 1];
      const nextIdx = lastQ
        ? nextPresentationStepIndex(
            currentPresentationStep,
            stepIndex,
            rebuilt,
            latestSections,
            logicRules,
            nextAnswers,
            style
          )
        : stepIndex + 1;

      if (nextIdx >= rebuilt.length) {
        await finishOrRedirect();
      } else {
        setStepIndex(nextIdx);
        setStepAnswers({});
      }
    } catch {
      toast.error(t('assessmentEngine.take.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [
    linkToken,
    responseId,
    currentPresentationStep,
    stepAnswers,
    answerMap,
    sections,
    presentationStyle,
    stepIndex,
    navigate,
    t,
  ]);

  const progress = useMemo(() => {
    if (isPagedMode) {
      if (!presentationSteps.length) return 0;
      return Math.round(((stepIndex + 1) / presentationSteps.length) * 100);
    }
    if (!questions.length) return 0;
    return Math.round(((index + 1) / questions.length) * 100);
  }, [isPagedMode, presentationSteps.length, stepIndex, index, questions.length]);

  const shell = (content: React.ReactNode, title?: React.ReactNode) => (
    <div className={AE_PUBLIC_PAGE}>
      <PublicHeader />
      <main className={`flex-1 px-2 py-2 ${AE_ASSESSMENT_CONTENT_WIDTH}`}>
        {title}
        {content}
      </main>
    </div>
  );

  const campaignTitle = (size: 'lg' | 'md' = 'lg') => (
    <TakeAssessmentCampaignTitle campaignName={campaign?.name} branding={branding} size={size} />
  );

  if (step === 'loading') {
    return shell(
      <div className="flex justify-center py-16">
        <div className={AE_SPINNER} />
      </div>
    );
  }

  if (step === 'error') {
    return shell(
      <p className="text-[var(--color-surface-text)]">{t('assessmentEngine.take.invalidLink')}</p>
    );
  }

  if (step === 'closed') {
    return shell(
      <p className="text-[var(--color-surface-text)]">
        {t('assessmentEngine.take.closed', {
          name: campaign?.name ?? t('assessmentEngine.take.thisAssessment'),
        })}
      </p>
    );
  }

  if (step === 'complete') {
    const shareUrl = linkToken ? assessmentEngineService.buildPublicUrl(linkToken) : '';
    const showShareLink =
      !!completionOptions.allow_share_link && distributionMode === 'public' && !!shareUrl;
    const showInviteOthers = !!completionOptions.allow_invite_others;
    const showCohortUpdates = !!completionOptions.allow_cohort_updates && !cohortSubscribed;

    const submitInvites = async () => {
      if (!linkToken || !responseId) return;
      const emails = inviteEmails.split(/[\n,;]+/).map((e) => e.trim()).filter(Boolean);
      if (!emails.length) return;
      setPostBusy(true);
      try {
        const { inserted } = await assessmentEngineService.postCompletionInvites(
          linkToken,
          responseId,
          emails
        );
        toast.success(t('assessmentEngine.take.invitesSent', { count: inserted }));
        setInviteEmails('');
      } catch {
        toast.error(t('assessmentEngine.take.invitesFailed'));
      } finally {
        setPostBusy(false);
      }
    };

    const subscribeCohort = async () => {
      if (!linkToken || !responseId) return;
      setPostBusy(true);
      try {
        await assessmentEngineService.postCompletionCohortSubscribe(linkToken, responseId);
        setCohortSubscribed(true);
        toast.success(t('assessmentEngine.take.cohortSubscribed'));
      } catch {
        toast.error(t('assessmentEngine.take.cohortSubscribeFailed'));
      } finally {
        setPostBusy(false);
      }
    };

    return shell(
      <div className={`${AE_CARD} space-y-4`}>
        <h1 className={`${AE_TITLE} mb-2 text-center`}>{t('assessmentEngine.take.thankYou')}</h1>
        <p className="text-[var(--color-surface-text)]">{t('assessmentEngine.take.submitted')}</p>

        {campaign?.respondent_framing?.closing?.message?.trim() && (
          <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-4 space-y-1">
            <h2 className="text-sm font-semibold text-[var(--color-surface-text)]">
              {t('assessmentEngine.take.whatsNext')}
            </h2>
            <p className="text-sm text-[var(--color-surface-text)] whitespace-pre-wrap">
              {campaign.respondent_framing.closing.message}
            </p>
          </div>
        )}

        {showInstantResults && linkToken && responseId && (
          <button
            type="button"
            className={AE_BTN_PRIMARY}
            onClick={() => navigate(assessmentEngineService.resultsUrl(linkToken, responseId))}
          >
            {t('assessmentEngine.take.viewMyResults')}
          </button>
        )}

        {showShareLink && (
          <div>
            <label className={AE_LABEL}>{t('assessmentEngine.take.shareLink')}</label>
            <div className="flex flex-wrap gap-2 items-center">
              <input readOnly className={`${AE_INPUT} flex-1 min-w-0`} value={shareUrl} />
              <CopyLinkButton url={shareUrl} />
            </div>
          </div>
        )}

        {showInviteOthers && (
          <div>
            <label className={AE_LABEL}>{t('assessmentEngine.take.inviteOthers')}</label>
            <p className={`${AE_MUTED} text-sm mb-2`}>{t('assessmentEngine.take.inviteOthersHint')}</p>
            <textarea
              className={`${AE_INPUT} w-full min-h-[80px]`}
              placeholder="colleague@example.com"
              value={inviteEmails}
              onChange={(e) => setInviteEmails(e.target.value)}
            />
            <button
              type="button"
              disabled={postBusy}
              className={`mt-2 ${AE_BTN_PRIMARY}`}
              onClick={submitInvites}
            >
              {t('assessmentEngine.take.sendInvites')}
            </button>
          </div>
        )}

        {showCohortUpdates && (
          <div className="space-y-2">
            <p className={AE_MUTED}>{t('assessmentEngine.take.cohortUpdatesHint')}</p>
            <button type="button" disabled={postBusy} className={AE_BTN_SECONDARY} onClick={subscribeCohort}>
              {t('assessmentEngine.take.subscribeCohort')}
            </button>
          </div>
        )}

        {cohortSubscribed && (
          <p className="text-sm text-[var(--color-surface-text)]">{t('assessmentEngine.take.cohortSubscribed')}</p>
        )}
      </div>,
      campaignTitle()
    );
  }

  if (step === 'email') {
    return shell(
      <div className={`${AE_CARD} space-y-4`}>
        <p className={AE_MUTED}>{t('assessmentEngine.take.emailPrompt')}</p>
        <div>
          <label className={AE_LABEL} htmlFor="ae-email">
            {t('assessmentEngine.live.email')}
          </label>
          <input
            id="ae-email"
            type="email"
            className={AE_INPUT}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <button type="button" disabled={saving} className={AE_BTN_PRIMARY} onClick={() => void continueFromEmail()}>
          {saving
            ? t('assessmentEngine.take.starting')
            : campaign?.email_verification_required === false
              ? t('assessmentEngine.take.continue')
              : t('assessmentEngine.take.sendVerificationCode')}
        </button>
      </div>,
      <>
        {campaignTitle()}
        <TakeAssessmentOpeningFraming framing={campaign?.respondent_framing} />
      </>
    );
  }

  if (step === 'otp') {
    return shell(
      <div className={`${AE_CARD} space-y-4`}>
        <p className={AE_MUTED}>{t('assessmentEngine.take.enterVerificationCode', { email })}</p>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          autoComplete="one-time-code"
          className={`${AE_INPUT_COMPACT} !w-36 tracking-widest text-center`}
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={saving}
            className={AE_BTN_SECONDARY}
            onClick={() => setStep('email')}
          >
            {t('assessmentEngine.take.back')}
          </button>
          <button
            type="button"
            disabled={saving || verificationCode.trim().length < 6}
            className={AE_BTN_PRIMARY}
            onClick={verifyAndStartSession}
          >
            {saving ? t('assessmentEngine.take.starting') : t('assessmentEngine.take.verifyAndContinue')}
          </button>
        </div>
      </div>,
      campaignTitle()
    );
  }

  if (step === 'welcome_back') {
    return shell(
      <div className={`${AE_CARD} space-y-4`}>
        <h2 className={AE_SECTION_TITLE}>{t('assessmentEngine.take.welcomeBack')}</h2>
        <p className={AE_MUTED}>{t('assessmentEngine.take.resumePrompt')}</p>
        <button type="button" className={AE_BTN_PRIMARY} onClick={() => setStep('questions')}>
          {t('assessmentEngine.take.resume')}
        </button>
      </div>,
      campaignTitle()
    );
  }

  if (isPagedMode && currentPresentationStep) {
    const totalSteps = presentationSteps.length;
    const isDemoStep = isDemographicsQuestionList(currentPresentationStep.questions);
    return shell(
      <div className={`${isDemoStep ? AE_CARD_COMPACT : AE_CARD} space-y-4`}>
        <div>
          <div className="h-2 bg-[var(--color-border)] rounded">
            <div className="h-2 bg-[var(--color-accent-bg)] rounded" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-[var(--color-secondary-text)] mt-1">
            {t('assessmentEngine.take.stepProgress', { current: stepIndex + 1, total: totalSteps })}
          </p>
        </div>
        <div>
          <h2 className={isDemoStep ? 'text-base font-semibold text-[var(--color-surface-text)]' : AE_SECTION_TITLE}>
            {currentPresentationStep.label}
          </h2>
          {currentPresentationStep.overview && (
            <p className={`${AE_MUTED} ${isDemoStep ? 'text-xs mt-0.5 mb-2' : 'mt-1 mb-4'}`}>
              {currentPresentationStep.overview}
            </p>
          )}
        </div>
        {isDemoStep ? (
          <DemographicQuestionsGrid
            questions={currentPresentationStep.questions}
            answersByQuestionId={stepAnswers}
            onChange={(questionId, val) => {
              setStepAnswers((prev) => ({ ...prev, [questionId]: val }));
              patchAnswerForQuestion(questionId, val);
            }}
          />
        ) : (
          currentPresentationStep.questions.map((q) => (
            <QuestionRenderer
              key={q.id}
              question={q}
              value={stepAnswers[q.id] ?? {}}
              onChange={(val) => {
                setStepAnswers((prev) => ({ ...prev, [q.id]: val }));
                patchAnswerForQuestion(q.id, val);
              }}
            />
          ))
        )}
        <div className="flex justify-between pt-1">
          <button
            type="button"
            className={`${AE_BTN_SECONDARY} disabled:opacity-40`}
            disabled={stepIndex === 0 || saving}
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
          >
            {t('assessmentEngine.take.back')}
          </button>
          <button type="button" disabled={saving} className={AE_BTN_PRIMARY} onClick={saveStepAndAdvance}>
            {saving
              ? t('assessmentEngine.take.saving')
              : stepIndex + 1 >= totalSteps
                ? t('assessmentEngine.take.submit')
                : t('assessmentEngine.take.next')}
          </button>
        </div>
      </div>,
      campaignTitle('md')
    );
  }

  const demoBlock = questionModeDemoQuestions;
  const progressCurrent = demoBlock && questionModeDemoRange
    ? questionModeDemoRange.start + 1
    : index + 1;

  return shell(
    <div className={`${demoBlock ? AE_CARD_COMPACT : AE_CARD} space-y-4`}>
      <div>
        <div className="h-2 bg-[var(--color-border)] rounded">
          <div className="h-2 bg-[var(--color-accent-bg)] rounded" style={{ width: `${progress}%` }} />
        </div>
        <p className="text-xs text-[var(--color-secondary-text)] mt-1">
          {t('assessmentEngine.take.questionProgress', {
            current: progressCurrent,
            total: questions.length,
          })}
        </p>
      </div>
      {demoBlock ? (
        <DemographicQuestionsGrid
          questions={demoBlock}
          answersByQuestionId={{
            ...answersByQuestionId,
            ...(current ? { [current.id]: answer } : {}),
          }}
          onChange={(questionId, val) => {
            patchAnswerForQuestion(questionId, val);
            if (current && questionId === current.id) setAnswer(val);
          }}
        />
      ) : (
        current && (
          <QuestionRenderer
            question={current}
            value={answer}
            onChange={(val) => {
              setAnswer(val);
              patchAnswerForQuestion(current.id, val);
            }}
          />
        )
      )}
      <div className="flex justify-between pt-1">
        <button
          type="button"
          className={`${AE_BTN_SECONDARY} disabled:opacity-40`}
          disabled={(questionModeDemoRange ? questionModeDemoRange.start : index) === 0 || saving}
          onClick={() => {
            if (questionModeDemoRange) {
              setIndex(Math.max(0, questionModeDemoRange.start - 1));
            } else {
              setIndex((i) => Math.max(0, i - 1));
            }
          }}
        >
          {t('assessmentEngine.take.back')}
        </button>
        <button type="button" disabled={saving} className={AE_BTN_PRIMARY} onClick={saveAndAdvance}>
          {saving
            ? t('assessmentEngine.take.saving')
            : (questionModeDemoRange ? questionModeDemoRange.end : index) + 1 >= questions.length
              ? t('assessmentEngine.take.submit')
              : t('assessmentEngine.take.next')}
        </button>
      </div>
    </div>,
    campaignTitle('md')
  );
};
